import { useMemo, useState, type FormEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import { createJourney, listJourneys } from "@/api/journeys";
import { listSegments } from "@/api/segments";
import { pickTitle } from "@/api/content";
import { Badge, Button, Card, Input, Skeleton } from "@/components";
import { useAuthStore } from "@/state/auth.store";
import { useLang } from "@/i18n/LanguageContext";

const STATUS_TONE: Record<string, "ok" | "warn" | "neutral"> = { active: "ok", draft: "warn", archived: "neutral" };
const STATUS: Record<string, { nl: string; en: string }> = {
  draft: { nl: "concept", en: "draft" },
  active: { nl: "actief", en: "active" },
  archived: { nl: "gearchiveerd", en: "archived" },
};

export function JourneysPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { t, lang } = useLang();
  const canEdit = useAuthStore((s) => s.user?.role) === "org_admin";

  const journeys = useQuery({ queryKey: ["journeys"], queryFn: ({ signal }) => listJourneys(signal) });
  const segments = useQuery({ queryKey: ["segments"], queryFn: ({ signal }) => listSegments(signal) });
  const companyWide = t("Iedereen (bedrijfsbreed)", "Everyone (company-wide)");
  const segName = (id: string | null) => (id ? segments.data?.find((s) => s.id === id)?.name ?? "segment" : companyWide);

  const [showForm, setShowForm] = useState(false);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [target, setTarget] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState("");

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q || !journeys.data) return journeys.data ?? [];
    return journeys.data.filter((j) => pickTitle(j.name_i18n, lang, j.name).toLowerCase().includes(q));
  }, [journeys.data, query, lang]);

  const create = useMutation({
    mutationFn: () => createJourney({ name, description: description || null, target_segment_id: target || null }),
    onSuccess: (j) => {
      qc.invalidateQueries({ queryKey: ["journeys"] });
      navigate(`/app/journeys/${j.id}`);
    },
    onError: (e) => setError(e instanceof Error ? e.message : t("Aanmaken mislukt", "Could not create")),
  });

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    create.mutate();
  }
  const statusLabel = (s: string) => (STATUS[s] ? t(STATUS[s].nl, STATUS[s].en) : s);

  return (
    <section>
      <div className="wrap">
        <div className="toolbar">
          <div>
            <p className="eyebrow">{t("Trajecten", "Journeys")}</p>
            <h2 style={{ fontSize: 30 }}>{t("Onboarding-trajecten", "Onboarding journeys")}</h2>
          </div>
          <div className="spacer" />
          {canEdit && (
            <Button variant={showForm ? "outline" : "primary"} onClick={() => setShowForm((v) => !v)}>
              {showForm ? t("Annuleren", "Cancel") : t("Nieuw traject", "New journey")}
            </Button>
          )}
        </div>

        {showForm && canEdit && (
          <Card>
            <form onSubmit={onSubmit}>
              {error && <div className="form-error">{error}</div>}
              <div className="field">
                <label htmlFor="jname">{t("Naam", "Name")}</label>
                <input id="jname" required value={name} onChange={(e) => setName(e.target.value)} placeholder={t("bijv. Onboarding Monteurs", "e.g. Technician onboarding")} />
              </div>
              <div className="field">
                <label htmlFor="jdesc">{t("Omschrijving", "Description")}</label>
                <input id="jdesc" value={description} onChange={(e) => setDescription(e.target.value)} />
              </div>
              <div className="field">
                <label htmlFor="jtarget">{t("Doelgroep", "Audience")}</label>
                <select id="jtarget" value={target} onChange={(e) => setTarget(e.target.value)}>
                  <option value="">{companyWide}</option>
                  {segments.data?.map((s) => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
              </div>
              <Button type="submit" disabled={create.isPending}>{create.isPending ? t("Bezig…", "Working…") : t("Aanmaken & inrichten", "Create & configure")}</Button>
            </form>
          </Card>
        )}

        <Card>
          {journeys.data && journeys.data.length > 0 && (
            <Input name="journey-search" placeholder={t("Zoek op naam…", "Search by name…")} value={query} onChange={(e) => setQuery(e.target.value)} />
          )}
          {journeys.isLoading ? (
            <Skeleton lines={5} />
          ) : filtered.length > 0 ? (
            <ul className="list-rows">
              {filtered.map((j) => (
                <li key={j.id}>
                  <span style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                    <Link to={`/app/journeys/${j.id}`} style={{ fontWeight: 700, color: "var(--ink)" }}>{pickTitle(j.name_i18n, lang, j.name)}</Link>
                    <span style={{ color: "var(--grey-text)", fontSize: 13 }}>→ {segName(j.target_segment_id)}</span>
                  </span>
                  <Badge tone={STATUS_TONE[j.status]}>{statusLabel(j.status)}</Badge>
                </li>
              ))}
            </ul>
          ) : (
            <p className="no-data-message">
              {journeys.data && journeys.data.length > 0
                ? t("Geen resultaten.", "No matches.")
                : `${t("Nog geen trajecten.", "No journeys yet.")} ${canEdit ? t("Maak er één aan.", "Create one.") : ""}`}
            </p>
          )}
        </Card>
      </div>
    </section>
  );
}
